import type { GridCellFormatter } from './types';

const padTwoDigits = (value: number): string => String(value).padStart(2, '0');

const fullWeekdayFormatter = new Intl.DateTimeFormat('en-150', {
  weekday: 'long',
});

const fullMonthFormatter = new Intl.DateTimeFormat('en-150', {
  month: 'long',
});

const parseDateValue = (value: unknown): Date | null => {
  const date = value instanceof Date ? new Date(value.getTime()) : new Date(String(value));
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDatePart = (date: Date): string =>
  [date.getFullYear(), padTwoDigits(date.getMonth() + 1), padTwoDigits(date.getDate())].join('/');

const getEn150WeekBasedYear = (date: Date): number => {
  const firstDayOfWeek = 1;
  const minimumDaysInFirstWeek = 1;
  const normalizedDayOfWeek = (date.getDay() - firstDayOfWeek + 7) % 7;
  const weekYearDate = new Date(date.getTime());

  weekYearDate.setDate(
    weekYearDate.getDate() + 7 - normalizedDayOfWeek - firstDayOfWeek - minimumDaysInFirstWeek
  );

  return weekYearDate.getFullYear();
};

export const formatDateAsYearMonthDay: GridCellFormatter = (value) => {
  const date = parseDateValue(value);
  return date ? formatDatePart(date) : null;
};

export const formatDateTimeAsYearMonthDayTwelveHour: GridCellFormatter = (value) => {
  const date = parseDateValue(value);
  if (!date) return null;

  const hours = date.getHours();
  const twelveHour = hours % 12 || 12;
  const period = hours < 12 ? 'AM' : 'PM';

  return `${formatDatePart(date)} ${padTwoDigits(twelveHour)}:${padTwoDigits(
    date.getMinutes()
  )} ${period}`;
};

export const formatDateAsDayOfMonthAndFullWeekday: GridCellFormatter = (value) => {
  const date = parseDateValue(value);
  if (!date) return null;

  return `${date.getDate()} ${fullWeekdayFormatter.format(date)}`;
};

export const formatDateTimeAsFullMonthDayAndWeekYear: GridCellFormatter = (value) => {
  const date = parseDateValue(value);
  if (!date) return null;

  return `${fullMonthFormatter.format(date)} ${date.getDate()}, ${getEn150WeekBasedYear(date)}`;
};
